import * as React from "react";
import { cn } from "./cn";
import { Container } from "./container";
import { WorldMapMotif } from "./world-map";

/**
 * How far the hero's parts move against the scroll, as a fraction of it. The
 * one source of these numbers: the effect below reads them, and the design
 * guide prints them, so the guide can never quote a figure the site has
 * stopped using.
 */
export const HERO_PARALLAX = {
  /** Background media travels at this fraction of scroll speed. */
  background: 0.4,
  /** The copy drifts at this fraction. */
  copy: 0.18,
  /** The copy has faded out by this fraction of the hero's own height. */
  fadeBy: 0.85,
} as const;

export interface HeroProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Small kicker above the title (e.g. "Term 2 2026"). */
  eyebrow?: React.ReactNode;
  /** Main headline. */
  title: React.ReactNode;
  /** Supporting paragraph under the title. */
  description?: React.ReactNode;
  /** Call-to-action buttons / links. */
  actions?: React.ReactNode;
  /** Optional media rendered beside the copy on large screens. */
  media?: React.ReactNode;
  /**
   * Optional media rendered as a full-bleed background filling the entire
   * hero, with the copy overlaid on top. Takes precedence over `media`.
   */
  backgroundMedia?: React.ReactNode;
  /** Show the dotted world-map brand backdrop. @default true */
  showMotif?: boolean;
  /**
   * Animate the hero: copy fades in with a staggered rise on mount, and the
   * background media parallax-scrolls (moves slower than the page) while the
   * copy drifts and fades as the hero scrolls away. Automatically disabled
   * for users with `prefers-reduced-motion`. @default true
   */
  animated?: boolean;
  /**
   * Measure the parallax against a scrolling box instead of the window. Only
   * the design guide passes this, so it can show the effect inside a small
   * frame; live pages leave it alone and scroll the window as usual.
   */
  scrollRoot?: React.RefObject<HTMLElement | null>;
  /**
   * Run the parallax even for a reader who has asked for reduced motion. Only
   * the design guide sets this, and only behind a button that reader pressed:
   * a specimen of the movement is worthless if it cannot move. Never set it on
   * a live page. @default false
   */
  forceMotion?: boolean;
}

/**
 * Primary page hero on the deep-teal brand surface with the dotted world-map
 * motif. Used on the home page and reusable for any landing-style banner.
 */
export const Hero = React.forwardRef<HTMLElement, HeroProps>(
  (
    {
      className,
      eyebrow,
      title,
      description,
      actions,
      media,
      backgroundMedia,
      showMotif = true,
      animated = true,
      scrollRoot,
      forceMotion = false,
      ...props
    },
    ref,
  ) => {
    const hasBackground = Boolean(backgroundMedia);
    const sectionRef = React.useRef<HTMLElement | null>(null);
    const backgroundRef = React.useRef<HTMLDivElement | null>(null);
    const copyRef = React.useRef<HTMLDivElement | null>(null);

    const setSectionRef = (node: HTMLElement | null) => {
      sectionRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    // Parallax on scroll: the background moves at ~40% of scroll speed and
    // the copy drifts at ~18% while fading, so the hero has depth as it
    // leaves the viewport. rAF-throttled; skipped for reduced motion.
    React.useEffect(() => {
      if (!animated) return;
      const section = sectionRef.current;
      if (!section) return;
      if (!forceMotion && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      // Normally the hero is measured against the top of the window. Given a
      // scrolling box it is measured against the top of that box instead —
      // same sums, different frame of reference.
      const root = scrollRoot?.current ?? null;
      const source: HTMLElement | Window = root ?? window;

      let frame = 0;
      const update = () => {
        frame = 0;
        const rect = section.getBoundingClientRect();
        const top = root ? rect.top - root.getBoundingClientRect().top : rect.top;
        const scrolled = Math.min(Math.max(-top, 0), rect.height);
        if (backgroundRef.current) {
          backgroundRef.current.style.transform = `translate3d(0, ${scrolled * HERO_PARALLAX.background}px, 0)`;
        }
        if (copyRef.current) {
          copyRef.current.style.transform = `translate3d(0, ${scrolled * HERO_PARALLAX.copy}px, 0)`;
          copyRef.current.style.opacity = String(
            Math.max(0, 1 - scrolled / (rect.height * HERO_PARALLAX.fadeBy)),
          );
        }
      };
      const onScroll = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      update();
      source.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      return () => {
        source.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (frame) cancelAnimationFrame(frame);
      };
    }, [animated, scrollRoot, forceMotion]);

    // Full literal class strings (Tailwind's scanner can't see composed
    // names) — one entrance step per copy element, top to bottom.
    const entrance = animated
      ? [
          "animate-fade-up",
          "animate-fade-up [animation-delay:120ms]",
          "animate-fade-up [animation-delay:240ms]",
          "animate-fade-up [animation-delay:360ms]",
        ]
      : [undefined, undefined, undefined, undefined];

    return (
      <section
        ref={setSectionRef}
        className={cn("relative isolate overflow-hidden bg-hero text-hero-fg", className)}
        {...props}
      >
        {hasBackground && (
          <>
            {/* Full-bleed background photo — any <img> passed is forced to
                cover the whole hero, no card rounding or shadow. */}
            <div
              ref={backgroundRef}
              className={cn(
                "pointer-events-none absolute inset-0 -z-20 will-change-transform [&_img]:h-full [&_img]:w-full [&_img]:rounded-none [&_img]:object-cover [&_img]:shadow-none [&_video]:h-full [&_video]:w-full [&_video]:object-cover",
                animated && "[&_img]:animate-hero-zoom",
              )}
            >
              {backgroundMedia}
            </div>
            {/* Scrim so the light copy stays legible over the photo. */}
            <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-hero/95 via-hero/80 to-hero/45" />
          </>
        )}
        {showMotif && !hasBackground && (
          <div className="pointer-events-none absolute inset-0 -z-10 text-hero-fg/10">
            <WorldMapMotif className="absolute right-0 top-1/2 h-[140%] w-[120%] -translate-y-1/2 translate-x-[15%]" />
          </div>
        )}
        <Container
          className={cn(
            hasBackground
              ? "py-[calc(6rem*var(--density,1))] sm:py-[calc(8rem*var(--density,1))] lg:py-[calc(10rem*var(--density,1))]"
              : "grid gap-10 py-[calc(4rem*var(--density,1))] sm:py-[calc(6rem*var(--density,1))] lg:grid-cols-2 lg:items-center",
          )}
        >
          <div ref={copyRef} className="max-w-2xl">
            {eyebrow && (
              <p className={cn("mb-3 text-sm font-bold uppercase tracking-wide text-hero-eyebrow", entrance[0])}>
                {eyebrow}
              </p>
            )}
            <h1 className={cn("font-heading text-4xl font-extrabold leading-tight sm:text-5xl", entrance[1])}>
              {title}
            </h1>
            {description && (
              <p className={cn("mt-5 text-lg leading-relaxed text-hero-fg/85", entrance[2])}>
                {description}
              </p>
            )}
            {actions && (
              <div className={cn("mt-8 flex flex-wrap gap-3", entrance[3])}>{actions}</div>
            )}
          </div>
          {!hasBackground && media && <div className="lg:justify-self-end">{media}</div>}
        </Container>
      </section>
    );
  },
);
Hero.displayName = "Hero";
